import areEqual from "fbjs/lib/areEqual";
import uniqWith from "lodash.uniqwith";

import { iterateEntries, iterateKeys } from "../../../../../Common/IterableUtils";
import { ICalculationFunctionCallContextProvider } from "../../Form/CustomizationContexts/CalculatorContext/ICalculationFunctionCallContextProvider";
import { BindingPath, nonRegularFieldName } from "../CoreTypes";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { IErrorManager } from "../ErrorManager/ErrorManager";
import { isChangeModelEventInfo, ModelEvent } from "../EventManagement/ModelEvents/ModelEvent";
import { IModel } from "../ModelStoring/Model";
import { Sections } from "../ModelStoring/Sections";
import { MaskRegistrator } from "../PathUtils/MaskRegistrator";
import { MaskResolver } from "../PathUtils/MaskResolver";
import { PathUtils } from "../PathUtils/PathUtils";
import { AbsoluteModelPath, createAbsoluteFromMask, PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { AbsoluteModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteModelPathSet";

import {
    CalculationAction,
    CalculationActions,
    CalculationFunctions,
    CalculationHooks,
    CalculationResult,
} from "./CalculationResources";
import { OperandsGetter } from "./OperandsGetter";

interface HookExecutionContext {
    basedMask: string;
    eventArgs?: ModelEvent;
    fn: string;
}

export interface ICalculator {
    calculateAll(model: IModel, initEvents: readonly ModelEvent[]): void;
    getArgumentsPaths(calcPath: AbsoluteModelPath): AbsoluteModelPath[];
    calculate(model: IModel, eventArgs: ModelEvent): void;

    loadFunctions(calculationHooks: CalculationHooks, calculationFunctions: CalculationFunctions): void;
}

interface MaskToHookMap {
    [mask: string]: CalculationActions;
}

export class Calculator implements ICalculator {
    private autocalcs: CalculationFunctions;
    private autocalcHooks: CalculationHooks;
    private hooksMasks: MaskRegistrator;
    private argsMasksCache: { [path: string]: string[] };
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly calculationFunctionCallContextProvider: ICalculationFunctionCallContextProvider;
    private readonly sections: Sections;
    private readonly errorManager: IErrorManager;

    public constructor(
        calculationFunctionCallContextProvider: ICalculationFunctionCallContextProvider,
        dataDeclarationHelper: DataDeclarationHelper,
        sections: Sections,
        errorManager: IErrorManager,
        calculationFunctions: CalculationFunctions,
        calculationHooks: CalculationHooks
    ) {
        this.calculationFunctionCallContextProvider = calculationFunctionCallContextProvider;
        this.sections = sections;
        this.errorManager = errorManager;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.autocalcs = calculationFunctions ?? {};
        this.autocalcHooks = calculationHooks ?? {};
        this.hooksMasks = new MaskRegistrator();
        this.argsMasksCache = {};
        for (const hookPathMask of iterateKeys(this.autocalcHooks)) {
            const [nodePath, valueName] = PathUtils.splitToPathAndValueName(hookPathMask);
            this.hooksMasks.registerMask(nodePath, valueName);
        }
    }

    public loadFunctions(calculationHooks: CalculationHooks, calculationFunctions: CalculationFunctions): void {
        this.autocalcs = calculationFunctions ?? {};
        this.autocalcHooks = calculationHooks ?? {};
        this.hooksMasks = new MaskRegistrator();
        this.argsMasksCache = {};
        for (const hookPathMask of iterateKeys(this.autocalcHooks)) {
            const [nodePath, valueName] = PathUtils.splitToPathAndValueName(hookPathMask);
            this.hooksMasks.registerMask(nodePath, valueName);
        }
    }

    public getArgumentsPaths(calcPath: AbsoluteModelPath): AbsoluteModelPath[] {
        const calcMask = this.dataDeclarationHelper.getMask(calcPath);
        const dependsOnMasks = calcMask != undefined ? this.getDependsOnMasks(calcMask) : [];

        return [
            ...AbsoluteModelPathSet.unique(
                dependsOnMasks
                    .map(mask => createAbsoluteFromMask(mask, PathTokens.each))
                    .map(mask => {
                        const dependsOnPath = mask.applyCommonInstancesFromAnotherPath(calcPath);
                        return dependsOnPath;
                    })
                    .flat()
            ),
        ];
    }

    public calculateAll(model: IModel, initEvents: readonly ModelEvent[]): void {
        let nextEventsCalculate = initEvents.filter(isChangeModelEventInfo);

        while (nextEventsCalculate.length > 0) {
            const calculatedEvents = this.calculateEvents(model, nextEventsCalculate);
            nextEventsCalculate = calculatedEvents.filter(isChangeModelEventInfo);
        }
    }

    public calculate(model: IModel, eventArgs: ModelEvent): void {
        let eventInfosToProcess = [eventArgs].filter(x => x.name != undefined);
        while (eventInfosToProcess.length > 0) {
            const hookContexts = this.createHooksContextSeparated(eventInfosToProcess);
            const eventsContext = model.beginGetEvents();
            for (const hook of uniqWith(hookContexts, areEqual)) {
                this.calculateHook(model, hook);
            }
            eventInfosToProcess = eventsContext.releaseAndGetEvent().filter(x => x.name != undefined);
        }
    }

    private calculateEvents(model: IModel, changeEvents: ModelEvent[]): ModelEvent[] {
        const fullMasks = this.findAllMasksByEvents(changeEvents);
        const hooksToExecute = this.createMaskToHookMap(fullMasks);
        const hooksContext = this.createHooksContext(hooksToExecute);

        const eventsContext = model.beginGetEvents();
        for (const hook of uniqWith(hooksContext, areEqual)) {
            this.calculateHook(model, hook);
        }
        return eventsContext.releaseAndGetEvent();
    }

    private createHooksContextSeparated(eventsToProcess: ModelEvent[]): HookExecutionContext[] {
        const result: HookExecutionContext[] = [];
        for (const eventInfo of eventsToProcess) {
            const mask = this.findMaskByEvent(eventInfo);
            if (mask == undefined) {
                continue;
            }
            const hooks = this.autocalcHooks[mask];
            if (hooks == undefined) {
                continue;
            }
            for (const hook of hooks) {
                result.push(this.createHookExecutionContext(hook, mask, eventInfo));
            }
        }
        return result;
    }

    private createHooksContext(hooksMap: MaskToHookMap): HookExecutionContext[] {
        const result: HookExecutionContext[] = [];
        for (const [mask, hookMap] of iterateEntries(hooksMap)) {
            result.push(...hookMap.map(hook => this.createHookExecutionContext(hook, mask, undefined)));
        }
        return result;
    }

    private createHookExecutionContext(
        hook: CalculationAction,
        hooksMask: string,
        eventArgs: undefined | ModelEvent
    ): HookExecutionContext {
        return {
            eventArgs: eventArgs,
            fn: hook.fn,
            basedMask: PathUtils.getAbsolutePath(hooksMask, hook.base),
        };
    }

    private createMaskToHookMap(fullMasks: string[]): MaskToHookMap {
        const filteredHooks: MaskToHookMap = {};

        for (const mask of fullMasks) {
            const autocalcHook = this.autocalcHooks[mask];
            if (autocalcHook != undefined) {
                filteredHooks[mask] = autocalcHook;
            }
        }
        return filteredHooks;
    }

    private findMaskByEvent(changeEvent: ModelEvent): undefined | string {
        if (changeEvent.fullPath == undefined) {
            throw new Error("InvalidProgramState");
        }
        return this.hooksMasks.getMasks(changeEvent.path, changeEvent.name)[0];
    }

    private findAllMasksByEvents(changeEvents: ModelEvent[]): string[] {
        const result: string[] = [];

        for (const changeEvent of changeEvents) {
            const mask = this.findMaskByEvent(changeEvent);
            if (mask != undefined && !result.includes(mask)) {
                result.push(mask);
            }
        }
        return result;
    }

    private calculateHook(model: IModel, hookContext: HookExecutionContext): void {
        const fullPath = hookContext.eventArgs?.fullPath;
        const pathByMask = this.getPathPartForMask(hookContext.basedMask, fullPath?.toLegacyPath(false));

        if (pathByMask == undefined) {
            const basePaths = MaskResolver.resolveMask(model, hookContext.basedMask);
            for (const path of basePaths) {
                this.executeHookFunctions(model, path, hookContext);
            }
        } else {
            this.executeHookFunctions(model, pathByMask, hookContext);
        }
    }

    private getPathPartForMask(mask: string, path: null | undefined | string): undefined | string {
        if (typeof mask !== "string" || typeof path !== "string") {
            return undefined;
        }
        if (mask === "") {
            return "";
        }

        const maskValues = PathUtils.split(mask);
        const pathValues = PathUtils.split(path).slice(0, maskValues.length);
        const isSame = (maskValue: string, i: number): boolean =>
            maskValue === "*" ? !isNaN(Number(pathValues[i])) : maskValue === pathValues[i];

        if (maskValues.every(isSame)) {
            return PathUtils.join(pathValues);
        }

        return undefined;
    }

    private executeHookFunctions(model: IModel, path: BindingPath, hook: HookExecutionContext): void {
        const operandsGetter = new OperandsGetter(model, this.dataDeclarationHelper, path, hook.eventArgs);
        const autocalcFunction = this.autocalcs[hook.fn];
        if (autocalcFunction == undefined) {
            throw new Error(`Calculation hook function ${hook.fn} not found.`);
        }
        const calculationResults = autocalcFunction.call(
            this.calculationFunctionCallContextProvider.getCalculationFunctionCallContext(model),
            operandsGetter
        );
        for (const calculationResult of calculationResults) {
            this.processCalculationResult(model, calculationResult, path);
        }
    }

    private getDependsOnMasks(calcMask: BindingPath): BindingPath[] {
        if (this.argsMasksCache[calcMask] == undefined) {
            this.buildArgumentsByMaskMap();
        }

        return this.argsMasksCache[calcMask] || [];
    }

    private buildArgumentsByMaskMap(): void {
        for (const [argumentPath, autocalcHooks] of iterateEntries(this.autocalcHooks)) {
            if (autocalcHooks == undefined) {
                continue;
            }
            const [argNodePath] = PathUtils.splitToPathAndValueName(argumentPath);
            for (const hook of autocalcHooks) {
                if (hook.res === undefined) {
                    continue;
                }
                const basePath = PathUtils.getAbsolutePath(argumentPath, hook.base);
                const resPath = PathUtils.getAbsolutePath(basePath, hook.res);

                if (!this.argsMasksCache[resPath]) {
                    this.argsMasksCache[resPath] = [];
                }
                if (!this.argsMasksCache[resPath]?.includes(argNodePath)) {
                    this.argsMasksCache[resPath]?.push(argNodePath);
                }
            }
        }
    }

    private processCalculationResult(
        model: IModel,
        [path, fieldName, value]: CalculationResult,
        basePath: string
    ): void {
        const absolutePath = PathUtils.getAbsolutePath(basePath, path);

        if (this.sections.isIgnoredPathByMasks(absolutePath)) {
            return;
        }

        if (fieldName === "error") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.errorManager.addErrorCached(model, absolutePath, value);
        }

        model.setViewModelField(absolutePath, nonRegularFieldName(fieldName), value);
    }
}
